// Tinaney Hub — client access to BYOI survey silos.
//
// A silo is a survey server a researcher runs on their own Supabase (see the
// Tinaney-Silo repo). This service registers silos, lists them for discovery,
// and — the key bit — mints a one-time ticket and exchanges it at the silo for a
// short-lived token, returning a Supabase client bound to that silo. Reads and
// writes then go straight to the silo; Tinaney never sees the survey data.

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';

export interface Silo {
  id: string;
  owner_id: string;
  name: string;
  silo_url: string;
  silo_anon_key: string;
  authenticate_url: string;
  logo_url: string | null;
  status: 'active' | 'disabled';
  paid_through: string | null;   // subscription paid up to this instant
  created_at: string;
}

export interface RegisterSiloInput {
  name: string;
  silo_url: string;
  silo_anon_key: string;
  authenticate_url: string;
  logo_url?: string;
}

// A survey as stored on a silo (see Tinaney-Silo schema.sql).
export interface SiloSurvey {
  id: string;
  title: Record<string, string> | null;
  description: Record<string, string> | null;
  status: string;
  reward_note: string | null;
  created_at: string;
}

export interface SiloQuestion {
  id: string;
  survey_id: string;
  order_index: number;
  question_type: string;
  question_text: Record<string, string> | null;
  config: any;
}

export interface SiloSession {
  client: SupabaseClient;
  role: 'owner' | 'respondent';
}

// --- Registry (Hub side) --------------------------------------------------

// Register (or re-register) a silo. Returns the one-time publish_secret to show
// the researcher once. Re-registering the same silo_url rotates the secret.
export async function registerSilo(input: RegisterSiloInput): Promise<{ silo_id: string; publish_secret: string }> {
  const { data, error } = await supabase.rpc('register_silo', {
    p_name: input.name,
    p_silo_url: input.silo_url.replace(/\/$/, ''),
    p_silo_anon_key: input.silo_anon_key,
    p_authenticate_url: input.authenticate_url.replace(/\/$/, ''),
    p_logo_url: input.logo_url || null,
  });
  if (error) throw error;
  return data as { silo_id: string; publish_secret: string };
}

// Silos owned by the current user (for the setup screen).
export async function listMySilos(): Promise<Silo[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('silos').select('*').eq('owner_id', user.id).order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as Silo[];
}

// All active silos (for discovery).
export async function listSilos(): Promise<Silo[]> {
  const { data, error } = await supabase
    .from('silos').select('*').eq('status', 'active').order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as Silo[];
}

export async function setSiloStatus(siloId: string, status: 'active' | 'disabled'): Promise<void> {
  const { error } = await supabase.from('silos').update({ status }).eq('id', siloId);
  if (error) throw error;
}

// Renew a silo's monthly subscription (charges the owner 5000 credits = $50 and
// extends paid_through by a month). Manual — there is no auto-charge.
export async function renewSilo(siloId: string): Promise<{ silo_id: string; paid_through: string }> {
  const { data, error } = await supabase.rpc('renew_silo', { p_silo_id: siloId });
  if (error) throw error;
  return data as { silo_id: string; paid_through: string };
}

// A silo's subscription is live when it's active and paid_through is in the future.
export function isSiloActive(silo: Silo): boolean {
  return silo.status === 'active' && !!silo.paid_through && new Date(silo.paid_through) > new Date();
}

// --- National data (Cambodia ledger) — paid per query in user credits ------

export interface NationalInsight {
  summary: string;
  strategic_recommendation?: string;
  references?: { source_name: string; context: string }[];
}

// Query the Cambodia national database (public_data_points) via the Hub's
// grounded oracle (public-insight-engine). Charges the caller's user credits
// (free monthly quota first); returns a grounded summary + cited references.
export async function queryNationalData(query: string): Promise<NationalInsight> {
  const { data, error } = await supabase.functions.invoke('public-insight-engine', { body: { query } });
  if (error) {
    let msg = error.message;
    try { const ctx = await (error as any).context?.json?.(); if (ctx?.error) msg = ctx.error; } catch { /* ignore */ }
    throw new Error(msg || 'Query failed.');
  }
  return data as NationalInsight;
}

// --- Connecting to a silo -------------------------------------------------

// Mint a Hub ticket, redeem it at the silo, and return a Supabase client bound
// to the silo with the minted token. Nothing is proxied through Tinaney.
export async function getSiloSession(silo: Silo): Promise<SiloSession> {
  const { data, error } = await supabase.rpc('create_silo_ticket', { p_silo_id: silo.id });
  if (error) throw error;
  const ticket_id = (data as any)?.ticket_id;
  if (!ticket_id) throw new Error('Could not create a silo ticket.');

  const res = await fetch(silo.authenticate_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ticket_id }),
  });
  if (!res.ok) throw new Error('The silo rejected the ticket. Is authenticate-hub-user deployed?');
  const { token } = await res.json() as { token?: string };
  if (!token) throw new Error('The silo did not return a token.');

  const client = createClient(silo.silo_url, silo.silo_anon_key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: { headers: { Authorization: `Bearer ${token}` } },
  });

  // The minted token carries app_metadata.silo_role; decode it locally to know
  // whether we connected as the owner or a respondent.
  let role: 'owner' | 'respondent' = 'respondent';
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload?.app_metadata?.silo_role === 'owner') role = 'owner';
  } catch { /* default respondent */ }

  return { client, role };
}

// --- Reading + taking a silo survey --------------------------------------

export async function listSiloSurveys(session: SiloSession): Promise<SiloSurvey[]> {
  const { data, error } = await session.client
    .from('surveys').select('*').eq('status', 'active').order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as SiloSurvey[];
}

export async function fetchSiloQuestions(session: SiloSession, surveyId: string): Promise<SiloQuestion[]> {
  const { data, error } = await session.client
    .from('survey_questions').select('*').eq('survey_id', surveyId).order('order_index', { ascending: true });
  if (error) throw error;
  return (data || []) as SiloQuestion[];
}

// --- Owner: analyse survey results with the silo's own Gemini key ----------

export interface SiloAnalysis {
  survey_id: string;
  response_count: number;
  insights: {
    summary: string;
    key_findings: string[];
    notable_patterns: string[];
    recommendations: string[];
  } | null;
  message?: string;
}

// Owner view of every survey on the silo (any status), for the analysis panel.
export async function listSiloSurveysAll(session: SiloSession): Promise<SiloSurvey[]> {
  const { data, error } = await session.client
    .from('surveys').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as SiloSurvey[];
}

// Run the silo's own `analyze-survey` Edge Function (owner token + the silo's
// own GEMINI_API_KEY). Returns null insights if there are no responses yet.
export async function analyzeSiloSurvey(session: SiloSession, surveyId: string): Promise<SiloAnalysis> {
  const { data, error } = await session.client.functions.invoke('analyze-survey', { body: { survey_id: surveyId } });
  if (error) {
    // Surface the function's own error body when present (e.g. missing GEMINI_API_KEY).
    let msg = error.message;
    try { const ctx = await (error as any).context?.json?.(); if (ctx?.error) msg = ctx.error; } catch { /* ignore */ }
    throw new Error(msg || 'Analysis failed.');
  }
  return data as SiloAnalysis;
}

// Submit a completed response straight to the silo. respondent_uid is forced
// server-side by the silo's trigger, so we never send it.
export async function submitSiloResponse(
  session: SiloSession,
  surveyId: string,
  answers: { question_id: string; answer_value: any }[]
): Promise<void> {
  const { data: resp, error: rErr } = await session.client
    .from('survey_responses').insert({ survey_id: surveyId }).select('id').single();
  if (rErr) throw rErr;
  const responseId = (resp as any).id;

  if (answers.length) {
    const rows = answers.map((a) => ({ response_id: responseId, question_id: a.question_id, answer_value: a.answer_value }));
    const { error: aErr } = await session.client.from('survey_answers').insert(rows);
    if (aErr) throw aErr;
  }

  const { error: cErr } = await session.client
    .from('survey_responses').update({ status: 'completed', completed_at: new Date().toISOString() }).eq('id', responseId);
  if (cErr) throw cErr;
}

// Small helper: pick a display string from an i18n JSON blob.
export function siloText(v: Record<string, string> | null | undefined, fallback = ''): string {
  if (!v) return fallback;
  return v.en || v.kh || Object.values(v)[0] || fallback;
}
